/**
 * net/credentials.ts — a credencial do receptor em disco.
 *
 * O `deviceToken` é entregue UMA VEZ (API-CONTRACT §4) e é a única coisa que
 * distingue esta TV das outras 500 da rede. Perder o storage = lojista pareando
 * de novo todo dia, que é como o produto morre.
 *
 * ⚠️ SEGURANÇA (regra dura, Playwright/curl em qualquer ambiente):
 *  - o token NUNCA vai para URL, query string, log ou console;
 *  - a credencial nunca é comparada por `===` de string de segredo aqui — quem
 *    valida é o backend, contra o hash guardado por ele (PLAYER.md §3).
 *
 * ponytail: `localStorage` conforme o PLAYER.md §2 (o token é pequeno e precisa
 * ser síncrono no boot). A MÍDIA vai para IndexedDB (`mediaCache.ts`) — é o que
 * não cabe no localStorage. Se um dia exigirem token não extraível, o salto é
 * WebCrypto + IndexedDB; a interface `CredentialStore` não muda.
 */

export interface ScreenCredentials {
  /** Credencial opaca do dispositivo. `dt_…` no backend. */
  deviceToken: string;
  /** Tela dona do token. Vem do poll, nunca do storage. */
  screenId: string;
  /** ISO de quando foi pareada — diagnóstico, e base do aviso de desatualizado. */
  pairedAt: string;
  /** Nome da tela, para a mensagem de espera ("TV Balcão"). */
  screenName?: string;
}

/** Contrato de armazenamento (plano §1.3): a credencial da TELA. */
const STORAGE_KEY = 'simpleindor.screen.v1';

/**
 * Chave do pareamento por código de 6 dígitos, que o web deixou de usar. O que
 * estiver ali veio do fluxo público — de uma conta que pode não ser a do
 * lojista (HYBRID §1) — então ela é APAGADA, nunca migrada (plano §3.7).
 */
const LEGACY_STORAGE_KEY = 'simpleindor.credentials.v1';

/** Apaga a credencial do fluxo público antigo. Idempotente, nunca lança. */
function purgeLegacy(store: KeyValueStore): void {
  try {
    store.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage indisponível: `load()` devolve null e a casca recomeça o vínculo.
  }
}

/** Fonte de armazenamento injetável (o `window.localStorage` no browser). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `localStorage` real, com degradação honesta quando o modo anônimo nega escrita. */
export function browserLocalStorage(): KeyValueStore | null {
  try {
    const storage = globalThis.localStorage;
    if (!storage) {
      return null;
    }
    // Modo anônimo/cookies desligados lança em `setItem` — testamos ANTES de
    // prometer que a credencial sobrevive ao reload.
    const probe = '__si_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null; // o receptor web nunca fica em tela preta por causa disso (PLAYER §2).
  }
}

/** Store em memória — o que o Node usa nos testes e o que cai quando não há `window`. */
export function memoryStore(initial: Record<string, string> = {}): KeyValueStore {
  const map = new Map<string, string>(Object.entries(initial));
  return {
    getItem: key => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: key => void map.delete(key),
  };
}

/**
 * Guarda/recupera a credencial. `load()` devolve `null` para TODO estado
 * aproveitável: ausente, JSON quebrado, ou campos faltando. A casca reage igual em
 * todos — volta para a tela de pareamento, sem crash e sem tela preta.
 */
export class CredentialStore {
  private readonly store: KeyValueStore;

  constructor(store: KeyValueStore) {
    this.store = store;
    purgeLegacy(store);
  }

  /** `true` quando o storage realmente persiste (recarregar a página preserva). */
  get isDurable(): boolean {
    return true;
  }

  load(): ScreenCredentials | null {
    let raw: string | null;
    try {
      raw = this.store.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
    if (raw === null || raw === '') {
      return null;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Storage corrompido (ou de outra versão do app): descarta e refaz.
      this.clear();
      return null;
    }
    if (typeof parsed !== 'object' || parsed === null) {
      this.clear();
      return null;
    }
    const candidate = parsed as Partial<ScreenCredentials>;
    if (typeof candidate.deviceToken !== 'string' || candidate.deviceToken === '') {
      this.clear();
      return null;
    }
    if (typeof candidate.screenId !== 'string' || candidate.screenId === '') {
      this.clear();
      return null;
    }
    return {
      deviceToken: candidate.deviceToken,
      screenId: candidate.screenId,
      pairedAt: typeof candidate.pairedAt === 'string' ? candidate.pairedAt : new Date(0).toISOString(),
      ...(typeof candidate.screenName === 'string' ? { screenName: candidate.screenName } : {}),
    };
  }

  save(credentials: ScreenCredentials): void {
    this.store.setItem(STORAGE_KEY, JSON.stringify(credentials));
  }

  /**
   * APAGA a credencial. Chamado quando o backend devolve
   * `401 DEVICE_TOKEN_REVOKED` (tela excluída no app ⇒ critério 10 do PLAYER §9):
   * sem isto, a TV ficaria batendo em 401 para sempre sem nunca mostrar nada.
   */
  clear(): void {
    try {
      this.store.removeItem(STORAGE_KEY);
    } catch {
      // Storage já indisponível: o estado desejado (sem credencial) já é o atual.
    }
  }
}
